/**
 * BabyCareStatsTimeline
 *
 * Grid-based timeline visualization for baby care statistics.
 * Shows activities vertically with time segments horizontally.
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import {
  Baby,
  Moon,
  Pill,
  Thermometer,
  Droplets,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import { BabyCareEntry, CareType } from '../index';

// Only show main care types (no notes in timeline)
const TIMELINE_CARE_TYPES: CareType[] = ['feeding', 'diaper', 'sleep', 'temperature', 'medication'];

// Icon colors
const CARE_ICON_COLORS: Record<CareType, string> = {
  feeding: 'text-pink-500',
  diaper: 'text-amber-500',
  sleep: 'text-indigo-500',
  medication: 'text-red-500',
  temperature: 'text-emerald-500',
  notes: 'text-blue-500',
};

const CARE_BG_COLORS: Record<CareType, string> = {
  feeding: 'bg-pink-500/75',
  diaper: 'bg-amber-500/75',
  sleep: 'bg-indigo-500/75',
  medication: 'bg-red-500/75',
  temperature: 'bg-emerald-500/75',
  notes: 'bg-blue-500/75',
};

// Common entry styling
const ENTRY_STYLE = 'border border-white/35';

const CARE_LABEL_KEYS: Record<CareType, string> = {
  feeding: 'entryTypesShort.feeding',
  diaper: 'entryTypesShort.diaper',
  sleep: 'entryTypesShort.sleep',
  medication: 'entryTypesShort.medication',
  temperature: 'entryTypesShort.temperature',
  notes: 'entryTypesShort.notes',
};

interface BabyCareStatsTimelineProps {
  entries: BabyCareEntry[];
  selectedDays: number;
  customStartDate?: string;
  customEndDate?: string;
}

function getIconComponent(type: CareType) {
  const icons = {
    feeding: Baby,
    diaper: Droplets,
    sleep: Moon,
    medication: Pill,
    temperature: Thermometer,
    notes: Thermometer,
  };
  return icons[type];
}

// Calculate trend comparing first half vs second half
function calculateTrend(
  entries: BabyCareEntry[],
  type: CareType
): { trend: 'up' | 'down' | 'neutral'; value: number } {
  const typeEntries = entries.filter(e => e.care_type === type);
  if (typeEntries.length < 2) return { trend: 'neutral', value: 0 };

  const sortedEntries = [...typeEntries].sort(
    (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
  );

  const midpoint = Math.floor(sortedEntries.length / 2);
  const firstHalfCount = midpoint;
  const secondHalfCount = sortedEntries.length - midpoint;

  if (firstHalfCount === 0) return { trend: 'neutral', value: 0 };

  const change = Math.round(((secondHalfCount - firstHalfCount) / firstHalfCount) * 100);

  if (change > 0) return { trend: 'up', value: change };
  if (change < 0) return { trend: 'down', value: Math.abs(change) };
  return { trend: 'neutral', value: 0 };
}

// Get segment label for time column
function getTimeSegments(selectedDays: number, customStartDate?: string): string[] {
  if (selectedDays === 1) {
    // Today: 24 hourly segments
    return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
  } else {
    // Multi-day: one segment per day
    const segments: string[] = [];
    const start = customStartDate ? new Date(customStartDate) : new Date();
    if (!customStartDate) {
      start.setDate(start.getDate() - selectedDays + 1);
    }
    start.setHours(0, 0, 0, 0);

    for (let i = 0; i < selectedDays; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      segments.push(date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }));
    }
    return segments;
  }
}

// Get entries for a specific segment (non-sleep types)
function getSegmentData(
  entries: BabyCareEntry[],
  type: CareType,
  segmentIndex: number,
  selectedDays: number,
  customStartDate?: string
): { count: number; value?: string } {
  const typeEntries = entries.filter(e => e.care_type === type);

  let segmentStart: Date;
  let segmentEnd: Date;

  if (selectedDays === 1) {
    // Today: hourly segments
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    segmentStart = new Date(today);
    segmentStart.setHours(segmentIndex);
    segmentEnd = new Date(segmentStart);
    segmentEnd.setHours(segmentIndex + 1);
  } else {
    // Multi-day: daily segments
    const start = customStartDate ? new Date(customStartDate) : new Date();
    if (!customStartDate) {
      start.setDate(start.getDate() - selectedDays + 1);
    }
    start.setHours(0, 0, 0, 0);
    segmentStart = new Date(start);
    segmentStart.setDate(segmentStart.getDate() + segmentIndex);
    segmentEnd = new Date(segmentStart);
    segmentEnd.setDate(segmentEnd.getDate() + 1);
  }

  const segmentEntries = typeEntries.filter(e => {
    const entryTime = new Date(e.recorded_at);
    return entryTime >= segmentStart && entryTime < segmentEnd;
  });

  if (segmentEntries.length === 0) {
    return { count: 0 };
  }

  // Special handling for temperature
  if (type === 'temperature') {
    const temps = segmentEntries
      .map(e => e.temperature_value)
      .filter((t): t is number => t !== null && t !== undefined);
    const maxTemp = temps.length > 0 ? Math.max(...temps) : undefined;
    return {
      count: segmentEntries.length,
      value: maxTemp ? `${maxTemp.toFixed(1)}°` : undefined,
    };
  }

  // For multi-day sleep: show total hours
  if (type === 'sleep' && selectedDays > 1) {
    const totalMinutes = segmentEntries
      .filter(e => e.ended_at)
      .reduce((sum, e) => {
        const start = new Date(e.recorded_at).getTime();
        const end = new Date(e.ended_at!).getTime();
        return sum + Math.floor((end - start) / (1000 * 60));
      }, 0);
    const hours = totalMinutes / 60;
    return {
      count: segmentEntries.length,
      value: hours > 0 ? `${hours.toFixed(1)}h` : undefined,
    };
  }

  return { count: segmentEntries.length };
}

// Get sleep entries as spanning bars (for today view)
interface SleepBar {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  durationText: string;
}

function getSleepBars(entries: BabyCareEntry[], tFn: (key: string) => string): SleepBar[] {
  const sleepEntries = entries.filter(e => e.care_type === 'sleep' && e.ended_at);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return sleepEntries
    .filter(e => {
      const start = new Date(e.recorded_at);
      const end = new Date(e.ended_at!);
      // Entry overlaps with today
      return start < tomorrow && end > today;
    })
    .map(entry => {
      const start = new Date(entry.recorded_at);
      const end = new Date(entry.ended_at!);

      // Clamp to today's bounds
      const clampedStart = start < today ? today : start;
      const clampedEnd = end > tomorrow ? tomorrow : end;

      const durationMs = clampedEnd.getTime() - clampedStart.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));

      let durationText: string;
      if (durationMinutes >= 60) {
        const hours = Math.floor(durationMinutes / 60);
        const mins = durationMinutes % 60;
        durationText = mins > 0 ? `${hours}:${String(mins).padStart(2, '0')} ${tFn('stats.hours')}` : `${hours} ${tFn('stats.hours')}`;
      } else {
        durationText = `${durationMinutes} ${tFn('stats.minuteShort')}`;
      }

      return {
        startHour: clampedStart.getHours(),
        startMinute: clampedStart.getMinutes(),
        endHour: clampedEnd.getHours(),
        endMinute: clampedEnd.getMinutes(),
        durationText,
      };
    });
}

export function BabyCareStatsTimeline({
  entries,
  selectedDays,
  customStartDate,
}: BabyCareStatsTimelineProps) {
  const { t } = useTranslation('babyCare');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isScrolled, setIsScrolled] = useState(false);

  // Track scroll (kept for potential future use)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollLeft > 10);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Filter state
  const [enabledTypes, setEnabledTypes] = useState<Set<CareType>>(
    new Set(TIMELINE_CARE_TYPES)
  );

  // Get available types (only those with entries)
  const availableTypes = useMemo(() => {
    const types = new Set<CareType>();
    entries.forEach(e => {
      if (TIMELINE_CARE_TYPES.includes(e.care_type)) {
        types.add(e.care_type);
      }
    });
    return types;
  }, [entries]);

  // Toggle filter
  const toggleType = (type: CareType) => {
    setEnabledTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Get time segments
  const segments = useMemo(
    () => getTimeSegments(selectedDays, customStartDate),
    [selectedDays, customStartDate]
  );

  // Calculate quick stats for 4 main types
  const quickStats = useMemo(() => {
    const mainTypes: CareType[] = ['feeding', 'diaper', 'sleep', 'temperature'];
    return mainTypes.map(type => {
      const typeEntries = entries.filter(e => e.care_type === type);
      const trend = calculateTrend(entries, type);

      let value: string = '-';
      let unit: string = '';

      if (typeEntries.length > 0) {
        if (type === 'sleep') {
          const totalMinutes = typeEntries
            .filter(e => e.ended_at)
            .reduce((sum, e) => {
              const start = new Date(e.recorded_at).getTime();
              const end = new Date(e.ended_at!).getTime();
              return sum + Math.floor((end - start) / (1000 * 60));
            }, 0);
          const hours = totalMinutes / 60;
          if (hours > 0) {
            value = hours.toFixed(1);
            unit = selectedDays === 1 ? 'h' : 'h/Tag';
          } else {
            value = String(typeEntries.length);
            unit = 'x';
          }
        } else if (type === 'temperature') {
          const temps = typeEntries
            .map(e => e.temperature_value)
            .filter((t): t is number => t !== null && t !== undefined);
          if (temps.length > 0) {
            value = Math.max(...temps).toFixed(1);
            unit = '°C max';
          }
        } else {
          value = String(typeEntries.length);
          unit = 'x';
        }
      }

      return { type, value, unit, trend };
    });
  }, [entries, selectedDays]);

  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-white/50">{t('stats.noEntriesInRange')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs - matching main page design */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {TIMELINE_CARE_TYPES.map(type => {
          const IconComponent = getIconComponent(type);
          const isEnabled = enabledTypes.has(type);
          const hasEntries = availableTypes.has(type);

          return (
            <button
              key={type}
              onClick={() => toggleType(type)}
              disabled={!hasEntries}
              className={cn(
                'filter-tab flex items-center gap-2 whitespace-nowrap',
                isEnabled && hasEntries && 'active',
                !hasEntries && 'opacity-40 cursor-not-allowed'
              )}
            >
              <IconComponent className={cn(
                'w-4 h-4',
                isEnabled ? 'text-white' : CARE_ICON_COLORS[type]
              )} />
              <span className="text-white">{t(CARE_LABEL_KEYS[type])}</span>
            </button>
          );
        })}
      </div>

      {/* Quick Stats - KPI Rows */}
      <div className="mb-2">
        {quickStats.map((stat, i) => {
          const IconComponent = getIconComponent(stat.type);

          return (
            <div key={stat.type}>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-white/50" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-white/70 text-sm">{t(CARE_LABEL_KEYS[stat.type])}</span>
                    {stat.trend.trend !== 'neutral' && stat.trend.value > 0 ? (
                      stat.trend.trend === 'up' ? (
                        <TrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-red-400" />
                      )
                    ) : (
                      <Minus className="w-3 h-3 text-white/40" />
                    )}
                  </div>
                </div>
                <span className="text-white text-2xl font-bold">
                  {stat.value}{stat.unit ? <span className="text-base font-normal text-white/50 ml-1">{stat.unit}</span> : null}
                </span>
              </div>
              {i < quickStats.length - 1 && (
                <div className="h-px bg-white/25" />
              )}
            </div>
          );
        })}
      </div>

      {/* Timeline Grid */}
      <div className="glass-effect rounded-[15px] overflow-hidden">
        <div className="flex">
          {/* Fixed Labels Column */}
          <div className="flex-shrink-0 flex flex-col items-center px-2">
            {/* Empty header cell */}
            <div className="h-10" />

            {/* Activity labels - icons only */}
            {TIMELINE_CARE_TYPES.filter(t => enabledTypes.has(t)).map(type => {
              const IconComponent = getIconComponent(type);

              return (
                <div key={type} className="h-12 flex items-center">
                  <IconComponent className={cn('w-5 h-5', CARE_ICON_COLORS[type])} />
                </div>
              );
            })}
          </div>

          {/* Scrollable Timeline Container */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-x-auto scrollbar-hide"
          >
            {/* Inner container with min-width for scrolling */}
            <div style={{ minWidth: `${segments.length * 50}px` }}>
              {/* Time headers row */}
              <div className="flex h-10 items-center">
                {segments.map((segment, idx) => (
                  <div
                    key={idx}
                    className="flex-shrink-0 flex items-center justify-center text-white/50 text-xs"
                    style={{ width: `${100 / segments.length}%` }}
                  >
                    {segment}
                  </div>
                ))}
              </div>

            {/* Activity rows */}
            {TIMELINE_CARE_TYPES.filter(t => enabledTypes.has(t)).map(type => {
              // For sleep in today view, render spanning bars
              if (type === 'sleep' && selectedDays === 1) {
                const sleepBars = getSleepBars(entries, t);
                const totalSegments = segments.length; // 24 for today

                return (
                  <div key={type} className="relative h-12">
                    {/* Grid cells as background */}
                    <div className="absolute inset-0 flex">
                      {segments.map((_, segmentIdx) => (
                        <div
                          key={segmentIdx}
                          className="flex-shrink-0 h-full"
                          style={{ width: `${100 / totalSegments}%` }}
                        />
                      ))}
                    </div>

                    {/* Sleep bars spanning multiple columns - percentage based */}
                    {sleepBars.map((bar, idx) => {
                      // Calculate position as percentage of 24 hours
                      const startHourDecimal = bar.startHour + bar.startMinute / 60;
                      const endHourDecimal = bar.endHour + bar.endMinute / 60;
                      const leftPercent = (startHourDecimal / 24) * 100;
                      const widthPercent = ((endHourDecimal - startHourDecimal) / 24) * 100;

                      return (
                        <div
                          key={idx}
                          className={cn(
                            'absolute top-1/2 -translate-y-1/2 h-7 rounded-lg flex items-center justify-center',
                            'text-white text-xs font-medium whitespace-nowrap',
                            CARE_BG_COLORS[type],
                            ENTRY_STYLE
                          )}
                          style={{
                            left: `${leftPercent}%`,
                            width: `${Math.max(widthPercent, 2)}%`,
                            minWidth: '40px',
                          }}
                        >
                          {bar.durationText}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              // Regular rows for non-sleep types
              return (
                <div key={type} className="flex h-12 items-center">
                  {segments.map((_, segmentIdx) => {
                    const data = getSegmentData(entries, type, segmentIdx, selectedDays, customStartDate);

                    return (
                      <div
                        key={segmentIdx}
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{ width: `${100 / segments.length}%` }}
                      >
                        {data.count > 0 && (
                          <span
                            className={cn(
                              'min-w-[28px] h-7 flex items-center justify-center rounded-lg text-white text-xs font-medium',
                              CARE_BG_COLORS[type],
                              ENTRY_STYLE
                            )}
                          >
                            {data.value || data.count}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="flex items-center justify-center gap-2 py-2 text-white/40 text-xs">
          <span>‹</span>
          <span>{t('stats.scrollHint')}</span>
          <span>›</span>
        </div>
      </div>

      {/* Filter hint */}
      <p className="text-center text-white/40 text-xs">
        {t('stats.toggleHint')}
      </p>
    </div>
  );
}
